<template>
  <div ref="searchContainerRef" tabindex="-1" class="relative z-[10]">
    <!-- Main Search Input Component -->
    <SearchMain
      ref="searchRef"
      v-model:model-value="search"
      :size="size"
      :type="type"
      :placeholder="placeholder"
      @search="handleSearchClick"
      @change="onChange"
      @focus="onFocus"
    >
    </SearchMain>

    <!-- Custom Search Results (for detail pages) -->
    <div v-if="customResultOpen" ref="resultRef">
      <slot name="result"></slot>
    </div>

    <!-- Search Recommendations Dropdown -->
    <div
      v-else-if="showRecommendTip && recommendRef.length"
      class="p-5 rounded-[12px] bg-back-0 border border-solid border-back-5 mt-[6px] absolute left-0 w-full search-overlay"
    >
      <div
        v-for="item in recommendRef"
        :key="item.question + item.model_type"
        :innerHTML="item.highlight_question"
        class="text-left px-3 py-2 cursor-pointer hover:bg-back-3 rounded-lg text-font-80"
        @click="onRecommendSelect(item)"
      ></div>
    </div>

    <!-- Search Tips Panel (Hot Searches & History) -->
    <div
      v-else-if="searchTipOpen"
      ref="searchResultContent"
      class="absolute left-0 w-full z-10 rounded-[12px] overflow-hidden border border-solid border-back-5 mt-[6px] p-5 bg-back-1 flex flex-col gap-[20px] search-overlay"
    >
      <!-- Hot Searches Section -->
      <div v-if="showHot && popularList?.data?.length">
        <div class="flex-y-center justify-between">
          <div class="text-14 font-semibold text-font-1 flex-y-center gap-[4px]">
            <iconpark-icon name="hot-fill" size="18"></iconpark-icon>
            {{ $t('re-men-sou-suo') }}：
          </div>
          <a
            class="flex-y-center gap-[2px] cursor-pointer text-14 text-font-70 hover:text-primary"
            @click.stop="handleHuanYiHuan"
          >
            <iconpark-icon name="arrow-refresh" size="16"></iconpark-icon>
            {{ $t('huan-yi-huan') }}
          </a>
        </div>
        <Popular
          ref="popularRef"
          :position="positionValue"
          class="mt-[10px]"
          :list="props.hotLength ? popularList?.data.slice(0, props.hotLength) : popularList?.data"
          :source="source || trackSource[props.area]"
          @select="v => onSearch(v)"
        />
      </div>

      <!-- Search History Section -->
      <History
        v-if="historyList?.data?.length"
        :direction="historyDirection"
        :data="historyList?.data"
        :historyLength="historyLength"
        @edit="edit"
        @search="v => onSearch(v)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { debounce } from 'lodash-es';
import SearchMain from './search.vue';
import History from './history.vue';
import Popular from './popular.vue';
import { SEARCH_HOT_WORD, SEARCH_RECORD_UNIQUE } from '~/constants';
import { editSearchHistory, getSearchHistory } from '~/api/index';
import { getSiteHotWord, get3DHotWord, getRecommendSearchWords } from '~/api/module/search';
import {
  SearchModelType,
  type RecommendSearchParams,
  type RecommendSearchResult,
} from '~/api/interface/search';
import { useLatestRequest } from '~/hooks/useLatestRequest';
import { ClickType } from './constant';
import { getRandomNames, MaterialType, EditIndexType } from '~/api/module/material';

// ===== Props and Emits =====
const props = withDefaults(
  defineProps<{
    modelValue?: string;
    placeholder?: string;
    size?: 'default' | 'large';
    historyDirection?: 'vertical' | 'inline';
    historyLength?: number;
    showResult?: boolean;
    showHot?: boolean;
    showRecommend?: boolean;
    position?: 'input' | 'empty' | string;
    area: SEARCH_RECORD_UNIQUE;
    type?: 'main' | 'default';
    source?: SOURCE;
    hotLength?: number;
  }>(),
  {
    size: 'default',
    hotLength: 5,
  },
);

const emit = defineEmits(['update:modelValue', 'search']);

// ===== Reactive References =====
const searchResultContent = ref();
const searchRef = ref();
const resultRef = ref();
const searchContainerRef = ref();
const startSearch = ref(true);
const userInfo = useUserInfo();
const showSearchTip = ref(false);
const showRecommendTip = ref(false);
const route = useRoute();
const beforeVal = ref(route.query?.keyword || '');
const allClear = ref(false);
const recommendRef = ref<RecommendSearchResult[]>([]);

// ===== Computed Properties =====
const search = computed({
  get: () => props.modelValue,
  set: v => {
    startSearch.value = true;
    emit('update:modelValue', v?.trim());
  },
});

const positionValue = computed(() => {
  return props.position as 'input' | 'empty' | undefined;
});

// ===== Constants and Mappings =====
// Hot word API actions mapping
const hotAction = {
  [SEARCH_RECORD_UNIQUE.TEMPLATE]: () =>
    getRandomNames({
      group_id: MaterialType.Template,
      size: 6,
      edit_index_type: EditIndexType.Change,
    }),
  [SEARCH_RECORD_UNIQUE.CHARACTER]: get3DHotWord,
  [SEARCH_RECORD_UNIQUE.SITE]: getSiteHotWord,
  [SEARCH_RECORD_UNIQUE.HELP_CENTER]: getSiteHotWord,
};

// Tracking source mapping
const trackSource = {
  [SEARCH_RECORD_UNIQUE.TEMPLATE]: SOURCE.TEMPLATE_CENTER,
  [SEARCH_RECORD_UNIQUE.CHARACTER]: SOURCE.CHARACTER,
  [SEARCH_RECORD_UNIQUE.SITE]: SOURCE.SITE_SEARCH,
  [SEARCH_RECORD_UNIQUE.HELP_CENTER]: SOURCE.HELP_CENTER,
};

// Model type mapping
const modelTypeMap = {
  [SEARCH_RECORD_UNIQUE.TEMPLATE]: SearchModelType.TEMPLATES,
  [SEARCH_RECORD_UNIQUE.CHARACTER]: SearchModelType.CHARACTER,
  [SEARCH_RECORD_UNIQUE.SITE]: SearchModelType.INDEX_DOCUMENT,
  [SEARCH_RECORD_UNIQUE.HELP_CENTER]: SearchModelType.HELP_CENTER,
};

// ===== Data Fetching =====
// Fetch search history
const { data: historyList, refresh: refreshHistory } = useAsyncData(() => {
  if (userInfo?.value?.user_id) {
    return getSearchHistory(props.area);
  }
  return Promise.resolve({
    code: 0,
    data: [],
  });
});

// Fetch popular/hot searches
const { data: popularList, refresh: popularRefresh } = useAsyncData(() => {
  if (!props.showHot) {
    return Promise.resolve({
      code: 0,
      data: [],
    });
  }
  const actions = hotAction[props.area];

  if (!actions) {
    return Promise.resolve({
      code: 0,
      data: [],
    });
  } else {
    return actions(
      props.area === SEARCH_RECORD_UNIQUE.SITE ? SEARCH_HOT_WORD.SITE : SEARCH_HOT_WORD.HELP,
      props.hotLength || 10,
    );
  }
});

// ===== Search Recommendation API =====
const { abort, request } = useLatestRequest<RecommendSearchParams>((..._args) => {
  const [abortControl, args] = _args;
  return getRecommendSearchWords(args, abortControl);
});

// Debounced search recommendation function
const searchRecommend = debounce(async (val: string) => {
  const payload: Record<string, any> = { q: val };
  if (props.area === SEARCH_RECORD_UNIQUE.CHARACTER) {
    payload.group_id = 1;
  } else {
    payload.model_type = modelTypeMap[props.area];
  }
  const recommend = await request(payload as RecommendSearchParams);
  recommendRef.value = recommend.data || [];
}, 500);

// ===== Event Handlers =====
// Handle "换一换" (refresh) button click
const handleHuanYiHuan = () => {
  trackEvent(TRACK_EVENT.CLICK_SITE_SEARCH, {
    source: props.source || trackSource[props.area],
    keywords: search.value,
    click_type: ClickType['换一换'],
  });
  popularRefresh();
};

// Edit search history
const edit = async (params: { search_record?: string; is_del?: boolean }) => {
  if (!userInfo?.value?.user_id) {
    return;
  }
  await editSearchHistory({
    ...params,
    search_area: props.area,
  });

  refreshHistory();
};

// Handle search action
const onSearch = (val: string) => {
  val = val?.trim();
  search.value = val;

  if (beforeVal.value === val) {
    return;
  }

  beforeVal.value = val;

  if (val) {
    if (!props.showResult && userInfo?.value?.user_id) {
      // Save to search history
      edit({
        search_record: val,
      });
    }
    resetTip();
    refreshHistory();
  }

  emit('search', val);
};

// Handle search button click
const handleSearchClick = (val: string, clickType?: ClickType) => {
  trackEvent(TRACK_EVENT.CLICK_SITE_SEARCH, {
    source: props.source || trackSource[props.area],
    keywords: val,
    click_type: clickType || ClickType['搜索按钮'],
  });
  onSearch(val);
};

// Handle input focus
const onFocus = () => {
  startSearch.value = true;

  const val = search.value?.trim();

  if (!val) {
    showSearchTip.value = true;
  } else {
    if (props.showRecommend) {
      // Get search recommendations
      searchRecommend(val);
      showRecommendTip.value = true;
    }
  }
};

// Reset all tip states
const resetTip = () => {
  showSearchTip.value = false;
  showRecommendTip.value = false;
  startSearch.value = false;
};

// Handle input blur
const onBlur = () => {
  resetTip();
  searchRef.value?.inputRef?.blur?.();
  // Only search if there's a value (except for help center)
  if (search?.value?.trim()) {
    onSearch(search?.value);
  }
};

// Handle input change
const onChange = (event: any) => {
  if (event.target.value.trim()) {
    if (props.showResult) {
      search.value = event.target.value;
      onSearch(event.target.value);
    }
    if (props.showRecommend) {
      // Get search recommendations
      searchRecommend(event.target.value);
      showRecommendTip.value = true;
    }
  } else if (!event.target.value) {
    if (event?.inputType !== 'deleteContentBackward') {
      allClear.value = true;
    }
    showSearchTip.value = true;
    showRecommendTip.value = false;
    onSearch('');
  }
};

// Handle click outside the search container
const handleClickOutside = (event: any) => {
  // If clicked outside the search container and not clearing
  if (
    !searchContainerRef?.value?.contains(event.target) &&
    (startSearch.value || showSearchTip.value) &&
    !allClear.value
  ) {
    onBlur();
    startSearch.value = false;
  } else {
    allClear.value = false;
  }
};

// Handle recommendation selection
function onRecommendSelect(item: RecommendSearchResult) {
  showRecommendTip.value = false;
  search.value = item.question;
  onSearch(item.question);
}

// ===== Computed Properties =====
// Whether to show custom search results
const customResultOpen = computed(() => !!(props.showResult && search.value?.trim()));

// Whether to show search tips panel
const searchTipOpen = computed(() => {
  return !!(
    showSearchTip.value &&
    (historyList.value?.data?.length || (props.showHot && popularList.value?.data?.length))
  );
});

// ===== Watchers =====
// Watch for user ID changes to refresh history
watch(
  () => userInfo?.value?.user_id,
  () => {
    refreshHistory();
  },
);

// Watch for recommendation tip visibility
watch(
  () => showRecommendTip.value,
  val => {
    if (!val) {
      recommendRef.value = [];
      searchRecommend.cancel();
      abort();
    }
  },
);

// ===== Lifecycle Hooks =====
onMounted(() => {
  // Add global click event listener
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  // Remove global click event listener
  document.removeEventListener('click', handleClickOutside);
});

// ===== Exposed Methods =====
defineExpose({
  onBlur,
  search: onSearch,
  editSearch: edit,
  refreshSearch: refreshHistory,
});
</script>

<style lang="scss" scoped>
.search {
  @apply relative rounded-[16px];
  :deep(.ant-input-group-addon) {
    display: none;
  }

  :deep(.ant-input) {
    &::placeholder {
      @apply text-font-40;
    }
  }
  // :deep(.ant-input-affix-wrapper) {
  //   border-color: transparent;
  // }
  .icon-size {
    @apply text-[18px];
  }

  .main {
    box-shadow: 0px 6px 20px 0px rgba(81, 80, 96, 0.2);
    border-radius: 20px;
  }
}

.search-large {
  box-shadow: 0px 3px 20px 0px rgba(21, 46, 106, 0.1);

  .icon-size {
    @apply text-[28px];
  }
  :deep(.ant-input-lg) {
    &::placeholder {
      @apply text-font-50;
    }
  }

  :deep(.ant-input-affix-wrapper-focused) {
    border-color: var(--purple-6) !important;
    opacity: 1;
  }
  :deep(.ant-input-affix-wrapper) {
    padding: 15px 24px;
    font-size: 18px;
    border-color: transparent;
    border-radius: 16px !important;
  }
  :deep(.ant-input-clear-icon) {
    @apply text-[18px];
  }
}

.search-overlay {
  box-shadow: 0px 6px 20px 0px rgba(81, 80, 96, 0.2);
}
</style>
